<!-- eslint-disable max-len -->
<template>
  <div class="relative">
    <div
      :class="[isInSettingTab ? 'mb-2' : 'info-row']"
    >
      <div
        :class="[isInSettingTab ? 'w-full mb-2' : 'info-col w-full md:w-1/2 md:pr-4']"
      >
        <div
          v-if="!isInSettingTab"
          class="mb-4 form-group"
        >
          <h5 class="font-bold">
            {{ $i18nPresenter.t('Name:') }}
          </h5>
          <aha-input
            class="mt-2"
            v-model="name"
            :maxLength="100"
          ></aha-input>
        </div>
        <div class="form-group">
          <h5 class="font-bold">
            {{ $i18nPresenter.t('Description:') }}
          </h5>
          <div class="description__text-area">
            <aha-textarea
              :placeholder="$i18nPresenter.t('Enter your description...')"
              v-model="description"
              @focus="focusDes"
              :maxLength="200"
              :rows="3"
            />
            <div
              class="description__ai-regenerate"
              v-tooltip="aiRegenerateTooltip"
              @click="handleAIRegenerateTemplateDescription()"
              v-if="isAIRegenerateButtonVisible"
              @mouseenter="trackMixpanel('Hover')"
            >
              <aha-icon
                name="arrows-clock-wise"
                width="15"
                height="15"
                viewBox="0 0 16 16"
              />
            </div>
            <AITemplateDescriptionLayout
              :isAIGenerateDescriptionFailed="isAIGenerateDescriptionFailed"
              :isAIGeneratingDescription="isAIGeneratingDescription"
              v-if="isAITemplateLayoutVisible"
              @closeAIGenerateTemplateDescription="closeAIGenerateTemplateDescription"
              @retryGenerateTemplateDescription="handleAIRegenerateTemplateDescription"
            />
          </div>
        </div>
      </div>
      <div
        :class="[isInSettingTab ? 'w-full mt-5' : 'info-col w-full mt-2 md:w-1/2 md:mt-0 mt:h-5/6']"
      >
        <h5 class="font-bold">
          {{ $i18nPresenter.t('Thumbnail image') }}
          <i
            class="fas fa-question-circle ml-1"
            v-tooltip="thumbnailTooltip"
          ></i>
        </h5>

        <div class="h-full mt-2 form-group">
          <!-- Button upload image -->
          <div class="button-add-image" v-if="!customThumbnailImage">
            <aha-button class="font-normal" @click="openModalUploadImage">
              <font-awesome-icon icon="image" />
              <span class="pl-1">{{ $i18nPresenter.t('Add image') }}</span>
            </aha-button>
          </div>
          <div
            class="flex items-center h-full slide-image-config horizontal layout"
            v-if="customThumbnailImage"
          >
            <div :class="[isInSettingTab ? 'image-box-setting' : 'image-box-modal']">
              <img :src="customThumbnailImage" alt>
            </div>
            <div>
              <div class="my-3">
                <a href="#">
                  <i class="pr-2 far fa-image text-primary-90" />
                  <span @click="openModalUploadImage">{{ $i18nPresenter.t('Change image') }}</span>
                </a>
              </div>
              <div class="my-3">
                <a href="#">
                  <i class="pr-2 fas fa-trash-alt text-primary-90" />
                  <span
                    @click="deleteThumbnail()"
                  >{{ $i18nPresenter.t('Remove') }}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="w-full mt-5"
      >
        <div
          v-if="!isLoadingCategories"
          class="form-group"
        >
          <h5 class="mb-2 font-bold">
            {{ $i18nPresenter.t('Categories:') }}
          </h5>
          <button
            v-for="item in allCategories"
            :key="`category-${item.categoryId}`"
            class="category-btn"
            :class="{ 'selected': item.selected == true }"
            @click="toogleClickedCategory(item)"
          >
            {{ $i18nPresenter.t(item.shortName) }}
          </button>
        </div>
        <div
          v-else
        >
          <h5 class="mb-2 font-bold">
            {{ $i18nPresenter.t('Categories:') }}
          </h5>
          <Loading class="mx-auto" />
        </div>
      </div>
    </div>
    <CropImage
      v-if="isCropImageModalShowing"
      :image-url="customThumbnailImageUrl"
      :isCropForMetaThumbnail="true"
      :aspect-ratio="16 / 9"
      @on-cancel="handleCancelCrop"
      @hideModal="isCropImageModalShowing = false"
      @handleCropImageFinished="handleCropImageFinished"
    />
    <UploadImageV2
      v-if="isUploadImageModalShowing"
      :type="'Thumbnail'"
      @hideModal="hideModalUploadImage"
      @callback="handleImageUpload"
      :tabs="uploadImageTabs"
    />
    <div class="form-group">
      <aha-button
        class="my-2"
        v-if="isInSettingTab"
        @click="updatePresentation()"
        :disabled="updatingPresentation"
        :submitting="updatingPresentation"
      >
        {{ $i18nPresenter.t('Save') }}
      </aha-button>
      <aha-button
        v-if="!isInSettingTab"
        @click="updateAndPublishPresentationAgain()"
        :disabled="updatingPresentation"
        :submitting="updatingPresentation"
        class="float-right mt-2 mb-2"
      >
        {{ $i18nPresenter.t('Done') }}
      </aha-button>
    </div>
    <div class="overlay-disable-setting" v-if="!canEditPresentation"></div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import UploadImageV2 from '@/components/presentations/UploadImage.v2.vue'
import CropImage from '@/components/presentations/CropImage'
import Config from '@/config'
import Utils from '@/util'
import CommunityApi from '@/api/community'
import OpenaiApi from '@/api/openai'
import eventCategories from '@/constant/eventCategories'
import { AI_TEMPLATE_DESCRIPTION } from '@/constant/eventActions'
import EventBus from '@/eventBus'
import CategoryApi from '@/api/category'
import Loading from '@/components/Loading'
import AITemplateDescriptionLayout from '@/components-v2/presentation-editor/editor-header/AITemplateDescriptionLayout.vue'
import Constants from '@/constant'
import ActiveTabNames from '@/constant/ActiveTabNames'

export default {
  components: {
    CropImage,
    UploadImageV2,
    Loading,
    AITemplateDescriptionLayout,
  },
  props: {
    presentation: {
      default: () => ({}),
      type: Object,
    },
    isInSettingTab: {
      type: Boolean,
      default: true,
    },
    hide: {
      type: Function,
      default: () => {},
    },
    currentCategories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      customThumbnailImageUrl: '_blank',
      isUploadImageModalShowing: false,
      isCropImageModalShowing: false,
      updatingPresentation: false,
      thumbnailTooltip: this.$i18nPresenter.t('Presentation thumbnail and description will appear when you publish this presentation to the Template Library or share a copy with others.'),
      allCategories: [],
      isLoadingCategories: false,
      isAIGeneratingDescription: false,
      isAIGenerateDescriptionFailed: false,
      DEFAULT_IMAGE_RATIOS: Constants.DEFAULT_IMAGE_RATIOS,
      uploadImageTabs: [ActiveTabNames.UPLOAD, ActiveTabNames.UNSPLASH],
    }
  },
  computed: {
    ...mapState({
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      canEditPresentation: (state) => state.canEditPresentation,
      currentPresentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
    }),
    description: {
      get() {
        return this.currentPresentation.description || ''
      },
      set(value) {
        const payload = {
          description: value,
        }
        this.patchPresentationAction(payload)
      },
    },
    name: {
      get() {
        return this.currentPresentation.name || ''
      },
      set(value) {
        const payload = {
          name: value,
        }
        this.patchPresentationAction(payload)
      },
    },
    customThumbnailImage: {
      get() {
        return this.currentPresentation.customThumbnailImage
      },
      set(value) {
        const payload = {
          customThumbnailImage: value,
        }
        this.patchPresentationAction(payload)
      },
    },
    hasDescription() {
      return this.description && this.description.trim().length
    },
    aiRegenerateTooltip() {
      return this.$i18nPresenter.t('Regenerate with AI')
    },
    allSlidesImported() {
      return this.currentPresentation && this.currentPresentation.slides &&
        this.currentPresentation.slides.every((slide) => slide.metadata && slide.metadata.isImportedFromPPT)
    },
    presentationHasSlideTitle() {
      return this.currentPresentation.slides.some((slide) => ((slide.title &&
        slide.title.trim().length) || slide.canvasBlocks) && !slide.metadata.isImportedFromPPT)
    },
    isAITemplateLayoutVisible() {
      return this.isAIGeneratingDescription || this.isAIGenerateDescriptionFailed
    },
    isAIRegenerateButtonVisible() {
      return !this.allSlidesImported && this.presentationHasSlideTitle &&
        this.currentPresentation.takenOverBy && this.currentPresentation.takenOverBy === this.currentUser.id
    },
  },
  async created() {
    await this.categoriesProcessing()
  },
  mounted() {
    this.setSelectedTabAction(ActiveTabNames.UPLOAD)
    this.customThumbnailImageUrl = this.presentation.customThumbnailImage
    if (!this.hasDescription && this.isAIRegenerateButtonVisible && this.$route.name !== 'Presentations') {
      this.handleAIRegenerateTemplateDescription()
      this.isAIGeneratingDescription = true
    }
    EventBus.$on('ai-template-description: generated', (doc) => {
      this._handleSetAITemplateDescriptionStatus(doc)
    })
  },
  destroyed() {
    EventBus.$off('ai-template-description: generated')
  },
  methods: {
    ...mapActions(['patchPresentationAction', 'setPresentationsAction', 'setSelectedTabAction']),
    openModalUploadImage() {
      this.isUploadImageModalShowing = true
      this.trackUploadimage()
    },
    async handleImageUpload(imageUrl) {
      this.imageUrl = imageUrl
      this.customThumbnailImageUrl = imageUrl
      this.isCropImageModalShowing = true
      this.isUploadImageModalShowing = false
    },
    hideModalUploadImage() {
      this.isUploadImageModalShowing = false
    },
    handleCancelCrop() {
      this.isCropImageModalShowing = false
      this.isUploadImageModalShowing = true
    },
    async handleResizeImage(imageUrl) {
      const extension = imageUrl.split('?')[0].split('.').pop()
      try {
        return this.$http.post(Config.api.resizeCustomThumbnailImage, {
          imageUrl,
          extension,
          expires: 10 * 365,
          presentationId: this.currentPresentation.id,
        })
      } catch (e) {
        Sentry.captureException(e)
        return false
      }
    },
    async handleCropImageFinished(data) {
      const { key, crop } = data
      const payload = {
        customThumbnailImage: key,
        isResizeCustomThumbnail: false,
      }
      this.patchPresentationAction({
        customThumbnailImage: crop,
      })
      this.isCropImageModalShowing = false
      await this._updatePresentationThumbnail(payload)
      this.handleResizeImage(data.crop)
    },
    deleteThumbnail() {
      this._updatePresentationThumbnail({
        customThumbnailImage: null,
        isResizeCustomThumbnail: false,
      })
      this.patchPresentationAction({
        customThumbnailImage: null,
      })
      this.customThumbnailImageUrl = null
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Presentation info > remove image', 'Thumbnail')
    },
    async _updatePresentationThumbnail(payload) {
      try {
        await this.$http.put(Config.api.updatePresentation + this.presentation.id, payload)
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Updating thumbnail failed. Please try again.'))
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Presentation info > upload image > Failed', 'Thumbnail')
      }
    },

    async updatePresentation() {
      const selectedCategories = []
      this.allCategories.forEach((item) => {
        if (item.selected) {
          selectedCategories.push(item.categoryId)
        }
      })
      const payload = {
        name: this.name.trim(),
        description: this.description.trim(),
        categoryIds: selectedCategories,
      }
      this.updatingPresentation = true
      try {
        await this.$http.put(Config.api.updatePresentation + this.presentation.id, payload)
        if (payload.categoryIds.length > 0) {
          this.updateLatestCategoriesForPresentation(payload.categoryIds)
        }
        Utils.showToastSuccess(this.$i18nPresenter.t('Presentation info updated.'))
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Presentation info > Save', 'Updated Description Successfully')
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Updating description failed. Please try again.'))
        Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Presentation info > Save > Failed', 'Updated Description Failed')
      } finally {
        this.updatingPresentation = false
      }
    },
    async updateAndPublishPresentationAgain() {
      if (this.name.trim().length === 0) {
        this.name = 'My new presentation'
      }
      const selectedCategories = []
      this.allCategories.forEach((item) => {
        if (item.selected) {
          selectedCategories.push(item.categoryId)
        }
      })
      const payload = {
        name: this.name.trim(),
        description: this.description.trim(),
        categoryIds: selectedCategories,
      }
      this.updatingPresentation = true
      try {
        await this.$http.put(Config.api.updatePresentation + this.presentation.id, payload)
        await CommunityApi.publishPresentation({
          presentationIds: [this.presentation.id],
          categoryIds: selectedCategories,
        })
        if (payload.categoryIds.length > 0) {
          this.updateLatestCategoriesForPresentation(payload.categoryIds)
        }
        Utils.showToastSuccess(this.$i18nPresenter.t('Template info updated.'))
      } catch (error) {
        Sentry.captureException(error)
        Utils.showToastError(this.$i18nPresenter.t('Something error, please try again in a few minutes.'))
      } finally {
        const template = {
          id: this.presentation.id,
          name: this.name.trim(),
          description: this.description.trim(),
          customThumbnailImage: this.customThumbnailImageUrl,
          categoryIds: selectedCategories,
        }
        const newPresentations = this.$store.state.presentations.map((presentation) => {
          if (presentation.id === this.presentation.id) {
            presentation.isPublicSearch = true
            presentation.name = this.name.trim()
            presentation.description = this.description.trim()
            if (this.customThumbnailImageUrl !== '_blank') {
              presentation.customThumbnailImage = this.customThumbnailImageUrl
            }
          }
          return presentation
        })
        if (selectedCategories && selectedCategories.length === 0) {
          // Remove all selected if no option or user remove all option.
          this.$store.state.presentation.PresentationsCategories = []
        }
        this.setPresentationsAction(newPresentations) // Update metadata: Presentation list..
        EventBus.$emit('update-library-after-published', template) // Update metadata: TemplateLibrary
        EventBus.$emit('update-template-detail-after-published', template) // Update metadata: TemplateDetail
        EventBus.$emit('update-template-preview-after-published', template) // Update metadata: TemplatePreview
        EventBus.$emit('update-template-category')
        Utils.mixpanelTracking(null, 'Edit template info > Done', null, null, {
          customThumbnailImage: template.customThumbnailImage,
          name: template.name,
          description: template.description,
          id: template.id,
        })
        this.updatingPresentation = false
        const el = document.getElementById('zsiq_float')
        if (el) {
          el.style.display = 'block'
        }
        this.hide()
      }
    },
    trackUploadimage() {
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Presentation info > upload image', 'Thumbnail')
    },
    focusDes() {
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Presentation info > Edit Description')
    },
    toogleClickedCategory(element) {
      element.selected = !element.selected
    },
    updateLatestCategoriesForPresentation(lists) {
      // eslint-disable-next-line sonarjs/no-identical-functions
      const categories = lists.map((item) => {
        return {
          categoryId: item,
          presentationId: this.presentation.id,
        }
      })
      const payload = {
        PresentationsCategories: categories,
      }
      this.$store.commit('updatePresentation', payload)
    },
    highlightCategories() {
      const categoriesList = this.currentCategories.map((item) => {
        return item.categoryId
      })
      this.allCategories.forEach((item) => {
        if (categoriesList.includes(item.categoryId)) {
          item.selected = true
        }
      })
    },
    async categoriesProcessing() {
      this.isLoadingCategories = true
      const res = await CategoryApi.getAllCategories()
      this.allCategories = res.data.map((item) => {
        return {
          categoryId: item.id,
          name: item.name,
          shortName: item.shortName,
          selected: false,
        }
      })
      this.highlightCategories()
      this.isLoadingCategories = false
    },
    closeAIGenerateTemplateDescription() {
      this.isAIGenerateDescriptionFailed = false
      this.isAIGeneratingDescription = false
    },
    _handleSetAITemplateDescriptionStatus(doc) {
      if (doc.error) {
        this._setAITemplateDescriptionFailed()
        return
      }
      if (!this.isAIGeneratingDescription) return
      this._setAITemplateDescriptionSucceeded(doc)
    },
    _setAIGenerateDescriptionDuration(value) {
      this.patchPresentationAction({
        aiGenerateDescriptionDuration: value,
      })
    },
    async _setAITemplateDescriptionSucceeded(doc) {
      this.description = doc

      const generateDuration = Date.now() - this.currentPresentation.aiGenerateDescriptionDuration
      this._setAIGenerateDescriptionDuration(generateDuration)
      this.trackMixpanel('Generate > Successful', {
        duration: this.currentPresentation.aiGenerateDescriptionDuration,
      })
      this.isAIGeneratingDescription = false
      await Utils.sleep(500)
      this._setAIGenerateDescriptionDuration(0)
    },
    async handleAIRegenerateTemplateDescription() {
      try {
        if (!this.isAIRegenerateButtonVisible) return
        this.trackMixpanel('Generate')
        this._setAIGenerateDescriptionDuration(Date.now())
        this.isAIGeneratingDescription = true
        this.isAIGenerateDescriptionFailed = false
        await OpenaiApi.generateTemplateDescription({
          id: this.presentation.id,
          uniqueAccessCode: this.currentPresentation.uniqueAccessCode,
        })
      } catch (error) {
        Sentry.captureException(error)
        this._setAITemplateDescriptionFailed()
      }
    },
    _setAITemplateDescriptionFailed() {
      this.isAIGeneratingDescription = false
      this.isAIGenerateDescriptionFailed = true
      this.trackMixpanel('Generate failed > Retry')
      this._setAIGenerateDescriptionDuration(0)
    },
    trackMixpanel(actions, customProps) {
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        `${AI_TEMPLATE_DESCRIPTION} > ${actions}`,
        this.description,
        null,
        {
          presentationId: this.currentPresentation.id,
          ...customProps,
        },
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.slide-image-config {
  background-color: #f2f2f2;
  .image-box-setting {
    margin: 1rem 10px 1rem 1rem;
    img {
      max-width: 150px;
      max-height: 90px;
      border-radius: 0.5rem;
    }
  }
  .image-box-modal {
    img {
      margin: auto 10px auto 1rem;
      @media (max-width: 767.78px) {
        margin: 1rem 10px 1rem 10px;
      }
      max-width: 150px;
      max-height: 90px;
      border-radius: 0.5rem;
    }
  }
}

.description__text-area {
  position: relative;
  width: 100%;
  &::v-deep {
    .aha-textarea {
      padding-right: 2rem;
      min-height: 86px;
      max-height: 116px;
    }
    .aha-textarea-limit {
      right: 1rem;
    }
  }
  .description__ai-regenerate {
    @apply absolute top-2 right-4 cursor-pointer;
  }
}
.overlay-disable-setting {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full;
}
.info-row {
  display: flex;
  flex-flow: row wrap;
}
.info-col {
  display: flex;
  flex-flow: column;
}
h5 {
  color: rgba(10, 10, 10, 1);
}
.category-btn {
  border-radius: 20px;
  border: 1px solid #D3D7E1;
  padding: 6px 12px;
  margin: 4px 2px;
  height: 32px;
  color: #525F7F;
  cursor: pointer;
  pointer-events: auto;
  letter-spacing: 0px;
  transition: 0.15s linear;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  @media screen and (min-width: 640px) {
    &:hover {
      border: 1px solid #348EF6;
    }
  }
}
.category-btn.selected {
  border: 1px solid #348EF6;
  color: #348EF6;
}
.fa-question-circle {
  color: #b7b7b7;
}
</style>
